<template>
  <div class="site-page site-page--wide nav-content-inset help-page">
    <div class="site-page__inner">
      <h1 class="site-page__title">帮助中心</h1>
      <p class="site-page__lead">
        CoolPeople 连接有趣的人、分享经验与认知。本页按当前系统功能说明怎么用；旧「常见问题」已合并到文末。
      </p>

      <nav class="help-toc" aria-label="目录">
        <a v-for="item in toc" :key="item.id" :href="'#' + item.id" @click.prevent="goSection(item.id)">
          {{ item.title }}
        </a>
      </nav>

      <section id="start" class="help-section">
        <h2>1. 开始使用</h2>
        <p>未登录也可浏览首页、编辑推荐、测评列表、课程列表与本页。登录后可购买、交友、创作、提现。</p>
        <ol>
          <li>点顶栏「登录」，用账号密码或站点已开放的第三方方式登录 / 注册。</li>
          <li>登录后进入「我的」完善资料；需要交友时再到「个人朋友信息」开启朋友功能。</li>
          <li>建议使用 Chrome、Firefox、Safari、Edge 等现代浏览器。</li>
        </ol>
        <p>
          <router-link to="/login">去登录</router-link>
          ·
          <router-link to="/about">关于 CoolPeople</router-link>
        </p>
      </section>

      <section id="account" class="help-section">
        <h2>2. 账户、资料与安全</h2>
        <ul>
          <li><strong>我的</strong>：余额、访客、喜欢、评论、测评记录、文章等入口。</li>
          <li><strong>个人朋友信息</strong>：顶部摘要看关键信息；现居/家乡在地图弹窗里选；简介可 AI 梳理。开启朋友功能须至少 3 张个人照片。</li>
          <li><strong>账号安全</strong>：修改登录名与密码。</li>
          <li><strong>站内信</strong>：审核、喜欢、提现、充值、分润、申诉、礼遇等通知；未读与已读分区明显，来源显示中文；顶栏头像有未读角标。</li>
          <li><strong>黑名单</strong>：拉黑后对方不再出现在喜欢等互动列表。</li>
        </ul>
      </section>

      <section id="blogs" class="help-section">
        <h2>3. 编辑推荐</h2>
        <p>顶栏「编辑推荐」进入列表，可用内容标签筛选。免费文章可直接阅读；付费文章未购买时只看到摘要，购买后看全文。</p>
        <ul>
          <li>写文章：登录后发布；普通用户发布后需管理员审核，通过才进公开列表。「我的文章」可看审核状态。</li>
          <li>改稿后会重新进入待审。可取消发布。</li>
          <li>消费记录里，申诉期内可申请退款（见「交易与申诉」）。</li>
        </ul>
        <p>
          <router-link to="/blogs">浏览编辑推荐</router-link>
          ·
          <router-link to="/blogs/create">写文章</router-link>
        </p>
      </section>

      <section id="exams" class="help-section">
        <h2>4. 心智测试</h2>
        <p>顶栏「心智测试」看列表，可按内容标签筛选。未登录也可答题；点「生成报告」时需要登录。</p>
        <ul>
          <li>免费测评可直接做；付费测评需购买后查看完整报告。</li>
          <li>「我的 → 心理测试」看本人全部已完成记录。</li>
          <li>创作者可在「我的创作」新增 / 编辑测评，提交后管理员审核通过才会出现在公开列表。本人与管理员未过审也可预览、试测。</li>
        </ul>
        <p><router-link to="/exames">去做测试</router-link></p>
      </section>

      <section id="courses" class="help-section">
        <h2>5. 知识课程</h2>
        <p>顶栏「知识课程」可浏览卡片与预览；进入具体课程或开始学习需登录。免费课直接学，付费课可余额购买，也可以使用创作者发出的兑换码。</p>
        <ul>
          <li>公开列表只展示：未删除、已上架、审核通过的课程。</li>
          <li>创作者在「我的创作」可简易建课（封面、音视频/B 站链接等），新建默认待审核。</li>
          <li>创作者可在自己的课程里生成兑换码，发给用户。用户在课程页核销后即可学习。核销记录和余额购买分开：创作者能看到谁在什么时间核销了哪张码，但不会按购买结算。</li>
          <li>改内容后需重新审核。下架或删除后前台不再展示。</li>
        </ul>
        <p>
          <router-link to="/knowledge/courses">浏览课程</router-link>
          ·
          <router-link to="/creator/works">我的创作</router-link>
        </p>
      </section>

      <section id="friends" class="help-section">
        <h2>6. 朋友</h2>
        <p>菜单「认识人」包含动态、找人、系统推荐、喜欢、谁看过我。使用找人和系统推荐前，请先在个人朋友信息里开启朋友功能。</p>
        <ul>
          <li><strong>找人 / 系统推荐</strong>：只出现已开放朋友信息的人；你自己未开启时无法搜索或看推荐。</li>
          <li><strong>喜欢</strong>：
            「我喜欢的人」只列出已开放的用户；
            「喜欢我的人」照片角标「喜欢我」，互相喜欢则角标「互相喜欢」；
            对方后来关闭朋友功能会标「未开放」，此时不能进详情。
          </li>
          <li>喜欢对方会发站内信；互相喜欢则双方各收一条。重复喜欢不重复通知。</li>
          <li><strong>谁看过我</strong>：「看我的人」照片角标「看过我」，未开放再标「未开放」；「我看过的人」只列出已开放用户，角标「我看过」。互相喜欢时优先显示「互相喜欢」。</li>
          <li><strong>动态</strong>：发动态、点赞、评论。动态被评论会收到站内信。</li>
          <li>朋友<strong>私信暂未开放</strong>，聊天按钮会提示，不会跳到不存在的页面。</li>
        </ul>
      </section>

      <section id="referral" class="help-section">
        <h2>7. 礼券与推荐</h2>
        <ol>
          <li>在「我的礼券」创建双享礼券，复制注册 / 绑定链接分享。</li>
          <li>新用户注册时可填券号，或打开链接绑定；已登录用户可随时在「我的 → 绑定礼券」补绑。</li>
          <li>绑定成功后推荐人会收到站内信，双方按规则获得礼遇；收益在「推荐收益」查看。</li>
          <li>申请<strong>臻享合伙人</strong>须先实名，管理员通过后可创建臻享礼券。</li>
        </ol>
        <p>
          <router-link to="/referral/bind-coupon">绑定礼券</router-link>
          ·
          <router-link to="/referral/coupons">我的礼券</router-link>
          ·
          <router-link to="/referral/earnings">推荐收益</router-link>
          ·
          <router-link to="/referral/partner-apply">申请合伙人</router-link>
        </p>
      </section>

      <section id="money" class="help-section">
        <h2>8. 实名、充值、提现与申诉</h2>
        <ul>
          <li><strong>实名认证</strong>：上传身份证正反面。审核中请等待；通过后仍可重新上传再提交，每次都会留下历史。新一次没通过时，仍沿用此前已通过的实名。提现与申请合伙人都要求已实名。</li>
          <li><strong>充值</strong>：<router-link to="/recharge">充值页</router-link>可用微信扫码（实时入账）或填写银行转账单据（后台核对后入账）。</li>
          <li><strong>提现</strong>：实名后填写银行卡。提交时会先扣余额；管理员拒绝则退回。进度在申请记录里看。</li>
          <li><strong>交易记录</strong>：申诉期内的消费可点「申诉退款」，填写理由并上传凭证。通过则全额退回余额；拒绝或要补材料会发站内信。</li>
        </ul>
        <p>
          <router-link to="/recharge">去充值</router-link>
          ·
          <router-link to="/transactions">交易记录</router-link>
        </p>
      </section>

      <section id="create" class="help-section">
        <h2>9. 创作与收入</h2>
        <p>「我的创作」管理你发布的课程与测评；「创作者收入」看销售分账。内容上架前通常要管理员审核，结果走站内信。</p>
        <ul>
          <li>测评：新增、编辑、逻辑删除/恢复、上下架、预览、试测、查看测试结果。</li>
          <li>课程：简易新建、改课、删除整课，并为自己的课程生成兑换码。</li>
          <li>VIP 等级仅展示，暂不支持自助购买。</li>
        </ul>
        <p>
          <router-link to="/creator/works">我的创作</router-link>
          ·
          <router-link to="/creator/income">创作者收入</router-link>
        </p>
      </section>

      <section id="comments" class="help-section">
        <h2>10. 评论</h2>
        <p>「我的」里「我评论 / 评论我」进入评论页，计数为全站累计。</p>
      </section>

      <section id="limits" class="help-section">
        <h2>11. 暂未开放</h2>
        <ul>
          <li>朋友私信、论坛、组织加入：前台会明确提示，不会假装成功。</li>
          <li>VIP 不可自助购买。</li>
          <li>下载 APP 入口仍在菜单，以页面说明为准。</li>
        </ul>
      </section>

      <section id="support" class="help-section">
        <h2>12. 反馈与联系</h2>
        <p>遇到问题可用意见反馈或联系我们留言（不必登录；已登录会记下账号）。管理员处理后，登录用户可能收到站内信。</p>
        <p>
          <router-link to="/feedback">意见反馈</router-link>
          ·
          <router-link to="/contact">联系我们</router-link>
          ·
          <router-link to="/team">团队介绍</router-link>
        </p>
      </section>

      <section id="faq" class="help-section">
        <h2>13. 常见问题</h2>
        <div class="site-page__card site-page__faq">
          <el-collapse v-model="activeFaq">
            <el-collapse-item v-for="(faq, index) in faqs" :key="index" :name="index">
              <template #title>
                <div class="faq-title">{{ faq.question }}</div>
              </template>
              <div class="faq-answer">{{ faq.answer }}</div>
            </el-collapse-item>
          </el-collapse>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
export default {
  name: 'HelpView',
  data() {
    return {
      activeFaq: [0],
      toc: [
        { id: 'start', title: '开始使用' },
        { id: 'account', title: '账户' },
        { id: 'blogs', title: '编辑推荐' },
        { id: 'exams', title: '心智测试' },
        { id: 'courses', title: '知识课程' },
        { id: 'friends', title: '朋友' },
        { id: 'referral', title: '礼券' },
        { id: 'money', title: '实名与资金' },
        { id: 'create', title: '创作' },
        { id: 'comments', title: '评论' },
        { id: 'limits', title: '暂未开放' },
        { id: 'support', title: '反馈' },
        { id: 'faq', title: '常见问题' }
      ],
      faqs: [
        {
          question: '礼券与推荐怎么玩？',
          answer:
            '在「我的」进入「我的礼券」创建双享礼券并分享注册/绑定链接；新用户注册时填券号或打开链接即可绑定。详见本页「礼券与推荐」。'
        },
        {
          question: '如何申诉退款？',
          answer:
            '在「交易记录」中，申诉期内的消费扣款可点击「申诉退款」，填写理由并上传凭证。管理员审核通过后全额退回余额；拒绝或要求补充材料会通过站内信通知。'
        },
        {
          question: '如何提现？',
          answer:
            '须先完成实名认证（头像菜单或「我的 → 创作与提现 → 实名认证」上传身份证），再在「提现申请」填写银行卡信息。管理员审核通过后财务批量打款，进度在申请记录中查看。'
        },
        {
          question: '如何申请臻享合伙人？',
          answer:
            '先完成实名认证，再在「我的 → 礼券与推荐 → 申请合伙人」或头像菜单「申请合伙人」提交资料，管理员审核通过后即可创建臻享礼券。'
        },
        {
          question: '如何开启朋友功能？',
          answer:
            '到「个人朋友信息」上传至少 3 张个人照片后打开开关。未开启时不能使用遇见/推荐，也不会出现在别人的搜索结果里。「我喜欢的人」也只显示已开放的用户。'
        },
        {
          question: '实名通过后还能换证件吗？',
          answer:
            '可以。通过后仍可重新选图提交；每次都会保存历史。新一次没通过时，仍沿用此前已通过的实名，不影响提现和合伙人。'
        },
        {
          question: 'CoolPeople 是什么？',
          answer:
            'CoolPeople 是一个连接有趣的人、分享经验与认知的平台，支持编辑推荐、心智测试、课程与社交发现等功能。'
        },
        {
          question: '如何注册账号？',
          answer: '点击顶栏「登录」，可使用账号密码或微信等方式注册/登录（以站点实际开放方式为准）。'
        },
        {
          question: '基础功能是否免费？',
          answer: '部分编辑推荐与测评可免费使用；付费测评、课程或文章需在平台内购买后查看完整报告或内容。'
        },
        {
          question: '如何联系客服或反馈问题？',
          answer: '可通过「联系我们」页面留言，或使用「意见反馈」提交建议与问题。'
        },
        {
          question: '推荐使用什么浏览器？',
          answer: '建议使用 Chrome、Firefox、Safari、Edge 等现代浏览器的最新版本，移动端与桌面端均可访问。'
        }
      ]
    }
  },
  mounted() {
    this.scrollToHash()
  },
  watch: {
    '$route.hash'() {
      this.scrollToHash()
    }
  },
  methods: {
    goSection(id) {
      this.$router.replace({ path: '/help', hash: '#' + id })
    },
    scrollToHash() {
      const id = (this.$route.hash || '').replace(/^#/, '')
      if (!id) return
      this.$nextTick(() => {
        const el = document.getElementById(id)
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
      })
    }
  }
}
</script>

<style scoped>
.help-toc {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin: 0 0 28px;
}

.help-toc a {
  display: inline-block;
  padding: 6px 12px;
  border-radius: 999px;
  background: var(--el-fill-color-light);
  color: var(--el-text-color-regular);
  font-size: 13px;
  text-decoration: none;
  line-height: 1.4;
}

.help-toc a:hover {
  color: var(--el-color-primary);
  background: var(--el-color-primary-light-9);
}

.help-section {
  margin-bottom: 32px;
  scroll-margin-top: 88px;
}

.help-section h2 {
  margin: 0 0 12px;
  font-size: 20px;
  font-weight: 600;
  color: var(--el-text-color-primary);
}

.help-section p,
.help-section li {
  font-size: 15px;
  line-height: 1.7;
  color: var(--el-text-color-regular);
}

.help-section ul,
.help-section ol {
  margin: 0 0 12px;
  padding-left: 1.3em;
}

.help-section li + li {
  margin-top: 6px;
}

.help-section a {
  color: var(--el-color-primary);
}
</style>
